import { useRef, useState } from 'react';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { useGAFormTracking } from '../../../../../shared/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import FormField from '@ramsey-design-system/form-field';
import {
  getRawText,
  getFormName,
  handleReferralResponse,
  useTrackStepStarted,
  useTrackStepError
} from '../../../utils';
import { phoneVerificationSchema } from '../../../schemas/phoneVerificationSchema';
import { VerificationCodeInput } from './VerificationCodeInput';
import { startVerification } from '../../../api';
import { useReferralContext } from '../../../context';
import {
  getTaxFullUrl,
  getVerificationBypass,
  hasValidVerificationBypass
} from '../../../../../shared/utils';
import { VerificationStatus } from '../../../enums';
import { createReferral } from '../../../api/createReferral';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { trackStepCompleted } from '../../../../../shared/analytics';
import { useWizard } from 'react-use-wizard';
import { StandardStepTitle, StepWrapper } from '../../stepComponents';
import { ProAgreementText, RamseyAgreementText } from '../../agreements';
import { ResubmitButton, SubmitButton } from '../../buttons';
import { ErrorMessage, SuccessMessage } from '../../messages';

const STEP_NAME = 'verification_step';
const STEP_LABEL = 'Enter verification code.';
const NO_PROS_MESSAGE =
  "Sorry, we weren't able to connect you with any pros. Try\u00A0again.";
const RESEND_FAILURE_MESSAGE =
  "Sorry, we couldn't resend a code. Try\u00A0again.";
const VERIFY_FAILURE_MESSAGE =
  "Sorry, we weren't able to verify your code. Try\u00A0again.";
const INVALID_CODE_MESSAGE =
  'Please enter a valid six\u2011digit verification\u00A0code.';

interface VerificationStepFields {
  verification_code: string;
}

interface VerificationResponseBody {
  validated: boolean;
  // Server-side proof that this phone number was verified; /api/referral rejects without it.
  // Absent on the bypass path, which the backend permits separately.
  verificationToken?: string;
}

function VerificationStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const [status, setStatus] = useState<VerificationStatus>(
    VerificationStatus.initial
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [invalidCodeMessage, setInvalidCodeMessage] = useState('');
  const { referral, updatePhoneVerificationCountDown, proInfo } =
    useReferralContext();
  const { phone } = referral;
  const proAgreement = getRawText(<ProAgreementText />);
  const ramseyAgreement = getRawText(<RamseyAgreementText />);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { activeStep } = useWizard();
  // Set synchronously, unlike `status`, which only lands after react-hook-form's async validation
  // resolves and a re-render. Holding Enter in the code input auto-repeats at ~30ms, which is
  // faster than that round trip on a slow device - the buttons are still enabled in that gap.
  const requestInFlight = useRef(false);
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<VerificationStepFields>({
    resolver: yupResolver(phoneVerificationSchema),
    defaultValues: { verification_code: '' }
  });

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  function handleResendCode() {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    setErrorMessage('');
    setSuccessMessage('');
    setStatus(VerificationStatus.resendingCode);

    startVerification(
      referral.phone,
      referral?.verification_type ?? 'sms',
      referral.postal_code
    )
      .then(() => {
        setStatus(VerificationStatus.resendCodeSuccess);
        updatePhoneVerificationCountDown(30);
        setSuccessMessage('Code resend\u00A0successful.');
      })
      .catch((e) => {
        setStatus(VerificationStatus.initial);
        setErrorMessage(RESEND_FAILURE_MESSAGE);
        trackError('system', RESEND_FAILURE_MESSAGE);
        console.error(e);
      })
      .finally(() => {
        requestInFlight.current = false;
      });
  }

  function handleInvalidVerificationResponse() {
    setStatus(VerificationStatus.initial);
    setInvalidCodeMessage(INVALID_CODE_MESSAGE);
    trackError('validation', INVALID_CODE_MESSAGE, 'verification_code');
    console.warn('Twilio responded with incorrect verification code');
  }

  function handleVerificationResponse(responseBody: VerificationResponseBody) {
    if (responseBody?.validated) {
      onValidSubmit(responseBody.verificationToken);
    } else {
      handleInvalidVerificationResponse();
    }
  }

  async function onValidSubmit(verificationToken?: string) {
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: [],
      answer_values: [],
      form_name: getFormName()
    });
    // When a bypass is active createReferral discards the token, so executing reCAPTCHA would
    // only feed synthetic/automated traffic into Google's risk model for our site key, which
    // depresses scores for real users. Skip it in that case.
    const bypassActive = hasValidVerificationBypass();

    if (!bypassActive && !executeRecaptcha) {
      console.error('Recaptcha not available');
      setErrorMessage(NO_PROS_MESSAGE);
      trackError('system', NO_PROS_MESSAGE);
      // Reset out of verifyingCode so the submit and resend buttons re-enable -
      // without this the user is left with no way to retry.
      setStatus(VerificationStatus.initial);
      return;
    }
    try {
      setStatus(VerificationStatus.submittingReferral);
      let recaptchaToken = '';
      if (!bypassActive && executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('verification_step');
      }

      createReferral(
        referral,
        recaptchaToken,
        proInfo,
        proAgreement,
        ramseyAgreement,
        verificationToken
      )
        .then((referralResponse) =>
          handleReferralResponse(
            referralResponse,
            referral,
            proInfo.partner_urn
          )
        )
        .catch((e) => {
          console.error('Create referral failure', e);
          setErrorMessage(NO_PROS_MESSAGE);
          trackError('system', NO_PROS_MESSAGE);
          setStatus(VerificationStatus.initial);
        });
    } catch (e) {
      console.error('reCAPTCHA execution failed', e);
      setErrorMessage(NO_PROS_MESSAGE);
      trackError('system', NO_PROS_MESSAGE);
      setStatus(VerificationStatus.initial);
    }
  }

  async function onSubmit(values: VerificationStepFields) {
    if (requestInFlight.current) {
      return;
    }
    requestInFlight.current = true;
    setErrorMessage('');
    setInvalidCodeMessage('');
    try {
      const response = await endVerification(phone, values.verification_code);
      handleVerificationResponse(response);
    } catch (e) {
      console.error(e);
      setErrorMessage(VERIFY_FAILURE_MESSAGE);
      trackError('system', VERIFY_FAILURE_MESSAGE);
      setStatus(VerificationStatus.initial);
    } finally {
      // By now `status` is either submittingReferral (buttons disabled by the status) or reset to
      // initial on failure, so the status checks take over from here.
      requestInFlight.current = false;
    }
  }

  function onInvalid(formErrors: FieldErrors<VerificationStepFields>) {
    trackError(
      'validation',
      formErrors.verification_code?.message ?? invalidCodeMessage,
      'verification_code'
    );
  }

  async function endVerification(phone: string, verificationCode: string) {
    const bypassVerificationQueryParam = getVerificationBypass();

    const payload =
      bypassVerificationQueryParam?.length > 0
        ? {
            phoneNumber: phone,
            verificationCode: verificationCode,
            bypassVerificationQuery: bypassVerificationQueryParam,
            postalCode: referral.postal_code
          }
        : {
            phoneNumber: phone,
            verificationCode: verificationCode
          };

    setStatus(VerificationStatus.verifyingCode);

    const responsePromise = await fetch(
      `${getTaxFullUrl()}/api/phone-validation-finish/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }
    );
    const verificationResponseBody = await responsePromise?.json();
    if (typeof verificationResponseBody?.validated !== 'boolean') {
      throw new Error('Invalid response from phone-validation-finish endpoint');
    }
    return verificationResponseBody;
  }

  return (
    <StepWrapper>
      <form
        ref={formRef}
        onSubmit={(event) => handleSubmit(onSubmit, onInvalid)(event)}
        noValidate
      >
        <StandardStepTitle>Enter verification code.</StandardStepTitle>
        <p className="text-3 text-left mb-4 sm:text-4">
          Next, you can look over the pro's info to get ready for when they
          {'\u00A0'}call.
        </p>
        <div className="space-y-2 mb-4">
          <FormField
            id="verification_code"
            invalid={!!errors?.verification_code || !!invalidCodeMessage}
            errors={[errors?.verification_code?.message ?? invalidCodeMessage]}
          >
            {() => (
              <Controller
                name="verification_code"
                control={control}
                render={({ field }) => (
                  <VerificationCodeInput
                    id="verification_code"
                    name={field.name}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={(code) => {
                      setInvalidCodeMessage('');
                      field.onChange(code);
                    }}
                    invalid={
                      !!errors?.verification_code || !!invalidCodeMessage
                    }
                  />
                )}
              />
            )}
          </FormField>
        </div>

        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        {successMessage && <SuccessMessage successMessage={successMessage} />}

        <div className="flex flex-col gap-1 items-center mb-4">
          <SubmitButton status={status} />
          <ResubmitButton status={status} onClick={handleResendCode} />
        </div>
        <ProAgreementText />
      </form>
    </StepWrapper>
  );
}

export { VerificationStep };
